import React, { useEffect } from "react";

//* Importing components from react-bootstrap
import {
  Form,
  FormControl as Input,
  InputGroup,
  Spinner,
} from "react-bootstrap";

//? Importing types
import { Discipline } from "./types/types";
import {
  QuestionDetails,
  QuestionType,
} from "../../../hooks/questions/types/types";
import { QuestionDetailsWithOriginalType } from "../form/index";

type ExamQuestionsSectionProps = {
  disciplines: Discipline[];
  disciplineSelect: Discipline;
  setDisciplineSelect: React.Dispatch<React.SetStateAction<Discipline>>;
  loadingInput: boolean;
  questionContent: string;
  setQuestionContent: React.Dispatch<React.SetStateAction<string>>;
  showAutoCompleteQuestion: boolean;
  setShowAutoCompleteQuestion: React.Dispatch<React.SetStateAction<boolean>>;
  loadingSearch: boolean;
  setLoadingSearch: React.Dispatch<React.SetStateAction<boolean>>;
  filteredQuestionsSearch: QuestionDetails[];
  questions: QuestionDetailsWithOriginalType[];
  setQuestions: React.Dispatch<
    React.SetStateAction<QuestionDetailsWithOriginalType[]>
  >;
  availableQuestions: QuestionDetails[];
  setShowModalTag: React.Dispatch<React.SetStateAction<boolean>>;
  timeout_: React.MutableRefObject<NodeJS.Timeout | undefined>;
  examType: QuestionType;
  editMode?: boolean;
  parseHtmlToText: (
    htmlString: string,
    sanitizeInput: (input: string) => string
  ) => string;
  DOMPurify: { sanitize: (input: string) => string };
  handleAddQuestion: (question: QuestionDetails) => void;
  handleRemoveQuestion: (questionId: string) => void;
};

/**
 * ExamQuestionsSection component.
 *
 * @param {ExamQuestionsSectionProps} props - The properties for the component.
 * @returns {JSX.Element} The rendered component.
 */
const ExamQuestionsSection: React.FC<ExamQuestionsSectionProps> = ({
  disciplines,
  disciplineSelect,
  setDisciplineSelect,
  loadingInput,
  questionContent,
  setQuestionContent,
  showAutoCompleteQuestion,
  setShowAutoCompleteQuestion,
  loadingSearch,
  setLoadingSearch,
  filteredQuestionsSearch,
  questions,
  setQuestions,
  availableQuestions,
  setShowModalTag,
  timeout_,
  parseHtmlToText,
  DOMPurify,
  examType,
  handleAddQuestion,
  handleRemoveQuestion,
}) => {
  // Whenever the examType changes, clear the search field and close the autocomplete
  useEffect(() => {
    setQuestionContent("");
    setShowAutoCompleteQuestion(false);
  }, [examType, setQuestionContent, setShowAutoCompleteQuestion]);

  // Function to get the representative text of the question type
  const getQuestionTypeText = (item: QuestionDetails): string => {
    if (item.subQuestions && item.subQuestions.length > 0) {
      const types = item.subQuestions.map((sq) => sq.type);
      const uniqueTypes = Array.from(new Set(types));
      if (uniqueTypes.length === 1) {
        return uniqueTypes[0] === "objective"
          ? "Questão objetiva"
          : "Questão dissertativa";
      }
      return "Questão Texto em Comun";
    }
    return "Sem subquestões";
  };

  // Function to highlight the searched term in the text
  const highlightText = (text: string, term: string): React.ReactNode => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={{ backgroundColor: "yellow" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const renderAutocompleteItem = (item: QuestionDetails, id: number) => {
    const mainText = String(item.statement ?? "").trim();
    const subText =
      item.subQuestions && item.subQuestions.length > 0
        ? item.subQuestions
            .map((sq) =>
              parseHtmlToText(String(sq.statement ?? ""), DOMPurify.sanitize)
            )
            .join(" ")
        : "";
    const combinedText = mainText === "" ? subText : mainText + " " + subText;

    return (
      <div
        key={id}
        onMouseDown={() => {
          if (!questions.find((q) => q.id === item.id)) {
            handleAddQuestion(item);
          }
          setQuestionContent("");
          setShowAutoCompleteQuestion(false);
        }}
        className="opcao_autocomplete"
        style={{
          padding: "8px",
          cursor: "pointer",
          borderBottom: "1px solid #eaeaea",
        }}
      >
        <b>{highlightText(combinedText, questionContent)}</b>
        <br />
        <small style={{ color: "#666" }}>{getQuestionTypeText(item)}</small>
      </div>
    );
  };

  return (
    <>
      <br />
      <InputGroup>
        <div className="flex fdcolumn flex1 margin-bottom">
          <Form.Label htmlFor="disciplineSelect">
            Selecione uma disciplina
          </Form.Label>
          <select
            id="disciplineSelect"
            title="Selecione uma disciplina"
            disabled={loadingInput}
            className="form-select"
            onChange={(e) => {
              const selectedDiscipline = disciplines.find(
                (item) => item.id.toString() === e.target.value
              );
              setDisciplineSelect(selectedDiscipline || { id: "", name: "" });
              setQuestionContent("");
            }}
            value={disciplineSelect.id.toString()}
          >
            <option value="">Selecione uma disciplina</option>
            {disciplines.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name}
              </option>
            ))}
          </select>

          {disciplineSelect.id && (
            <div
              style={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}
            >
              <a
                onClick={() => {
                  setQuestionContent("");
                  let newQuestions = [...questions];
                  availableQuestions.forEach((item) => {
                    if (!newQuestions.find((q) => q.id === item.id)) {
                      // Adiciona a questão com originalType definido como examType
                      newQuestions.push({
                        ...item,
                        originalType: examType,
                      });
                      handleAddQuestion(item);
                    }
                  });
                }}
                style={{
                  textAlign: "left",
                  fontSize: 15,
                  marginRight: 20,
                  cursor: loadingSearch ? "not-allowed" : "pointer",
                  textDecoration: "none",
                  opacity: loadingSearch ? 0.5 : 1,
                }}
                className="link-success"
              >
                Selecionar todas as questões desta disciplina
              </a>

              <a
                onClick={() => {
                  setShowModalTag(true);
                }}
                style={{
                  textAlign: "left",
                  fontSize: 15,
                  cursor: loadingSearch ? "not-allowed" : "pointer",
                  textDecoration: "none",
                  opacity: loadingSearch ? 0.5 : 1,
                }}
                className="link-success"
              >
                Selecionar questões por uma tag
              </a>
            </div>
          )}
        </div>
      </InputGroup>

      {disciplineSelect.id && (
        <InputGroup>
          <div
            className="flex fdcolumn flex1 margin-bottom"
            style={{ position: "relative" }}
          >
            <Form.Label>
              Busque questões da disciplina {disciplineSelect.name}
            </Form.Label>
            <Input
              disabled={loadingInput}
              placeholder="Pesquise a questão pelo enunciado"
              value={questionContent}
              onBlur={() => {
                timeout_.current = setTimeout(() => {
                  setShowAutoCompleteQuestion(false);
                }, 500);
              }}
              onFocus={() => {
                if (timeout_.current) {
                  clearTimeout(timeout_.current);
                }
                setShowAutoCompleteQuestion(true);
              }}
              onChange={(e) => {
                setQuestionContent(e.target.value);
              }}
            />
            {showAutoCompleteQuestion && questionContent && (
              <div
                className="autocomplete-dropdown"
                style={{
                  width: "100%",
                  position: "absolute",
                  zIndex: 20,
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0px 2px 5px rgba(0,0,0,0.15)",
                  maxHeight: "50vh",
                  overflowY: "auto",
                  top: "70px",
                }}
              >
                {!loadingSearch &&
                  availableQuestions.map((item, id) =>
                    renderAutocompleteItem(item, id)
                  )}
                {!loadingSearch && availableQuestions.length === 0 && (
                  <div
                    className="opcao_autocomplete"
                    style={{ padding: "8px", color: "#666" }}
                  >
                    {questionContent.length === 0
                      ? "Digite o enunciado da questão que deseja buscar"
                      : "Nenhuma questão encontrada com este termo"}
                  </div>
                )}
                {loadingSearch && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      padding: "8px",
                    }}
                  >
                    <Spinner animation="border" variant="primary" size="sm" />
                  </div>
                )}
              </div>
            )}
          </div>
        </InputGroup>
      )}
    </>
  );
};

export default ExamQuestionsSection;
